/**
 * `?perf=1` probes: sim tick time (host), React commit time, frame time, draw
 * calls and data-channel traffic, read once a second by `PerfOverlay`. Off
 * (and free) without the flag; with it, the data-channel counters patch
 * `RTCDataChannel` at load, before any peer connects.
 */

export const PERF_ENABLED =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf') === '1';

/** Samples since the last read: their mean and max. */
export class PerfStat {
  private sum = 0;
  private max = 0;
  private count = 0;

  add(value: number): void {
    this.sum += value;
    this.count++;
    if (value > this.max) this.max = value;
  }

  /** The window's mean/max (null with no samples), then a fresh window. */
  take(): { mean: number; max: number; count: number } | null {
    const out =
      this.count > 0 ? { mean: this.sum / this.count, max: this.max, count: this.count } : null;
    this.sum = 0;
    this.max = 0;
    this.count = 0;
    return out;
  }
}

export const perf = {
  /** One sim `tick()`, on the host only. */
  simTick: new PerfStat(),
  /** GameVisuals3D render to its layout effects: the HUD tree's render and commit. */
  commit: new PerfStat(),
  /** Time between rendered canvas frames. */
  frame: new PerfStat(),
  /** `renderer.info.render.calls` of the last frame. */
  drawCalls: 0,
  /** Data-channel bytes (string length for text) since the last read. */
  bytesOut: 0,
  bytesIn: 0,
};

function sizeOf(data: unknown): number {
  if (typeof data === 'string') return data.length;
  if (data instanceof ArrayBuffer) return data.byteLength;
  if (ArrayBuffer.isView(data)) return data.byteLength;
  if (data instanceof Blob) return data.size;
  return 0;
}

/** Counts every data-channel message sent and received (phop's snapshots dominate). */
function installChannelCounters(): void {
  if (typeof RTCDataChannel === 'undefined') return;
  const proto = RTCDataChannel.prototype;
  const send = proto.send;
  proto.send = function (this: RTCDataChannel, data: unknown) {
    perf.bytesOut += sizeOf(data);
    return (send as (this: RTCDataChannel, data: unknown) => void).call(this, data);
  } as typeof proto.send;
  const onmessage = Object.getOwnPropertyDescriptor(proto, 'onmessage');
  if (!onmessage?.set || !onmessage.get) return;
  const { get, set } = onmessage;
  Object.defineProperty(proto, 'onmessage', {
    ...onmessage,
    get,
    set(this: RTCDataChannel, handler: ((event: MessageEvent) => unknown) | null) {
      set.call(
        this,
        handler &&
          function (this: RTCDataChannel, event: MessageEvent) {
            perf.bytesIn += sizeOf(event.data);
            return handler.call(this, event);
          }
      );
    },
  });
}

if (PERF_ENABLED) installChannelCounters();
